import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { X, ShoppingCart, Calendar, MapPin, Trash2, ChevronDown, ChevronUp, Smartphone, Speaker, Video, Sliders, Info, Users } from "lucide-react";
import { PlanStorage, type ShowWithPackage } from "@/lib/plan-storage";
import { usePlanStore } from "@/stores/plan-store";
import { PlanSidebarState } from "@/lib/plan-sidebar-state";
import { useAuth } from "@/lib/auth";
import { usePricingVisibility } from "@/lib/pricing-visibility";
import { formatDate } from "@/modules/calculator";
import { Link } from "wouter";

interface PlanSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  className?: string;
}

// Stable empty objects to prevent re-render loops
const EMPTY_SHOWS = {};
const EMPTY_PACKAGES = {};

// Format venue capacity for display (e.g., 5000 -> "5k capacity")
const formatCapacity = (capacity: number | null | undefined): string => {
  if (!capacity) return '';
  if (capacity >= 1000) {
    return `${Math.round(capacity / 1000)}k capacity`;
  }
  return `${capacity} capacity`;
};

export default function PlanSidebar({ isOpen, onToggle, className }: PlanSidebarProps) {
  const { user } = useAuth();
  const { isPricingVisible } = usePricingVisibility();
  const [isExpanded, setIsExpanded] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const userKey = user?.id ?? 0;

  // Subscribe directly to the user's shows and packages using stable userKey
  // This avoids re-creating the selector on every render
  const userShows = usePlanStore(useCallback((state) => state.shows[userKey], [userKey]));
  const userPackages = usePlanStore(useCallback((state) => state.packages[userKey], [userKey]));
  const isUpdatingPricing = usePlanStore((state) => state.isUpdatingPricing);

  // Use stable empty objects if user data doesn't exist
  const shows = userShows || EMPTY_SHOWS;
  const packages = userPackages || EMPTY_PACKAGES;

  // Debug logging
  useEffect(() => {
    console.log('🔍 [SIDEBAR] State changed:', {
      shows,
      packages,
      userId: user?.id,
      showCount: Object.keys(shows).length,
      packageCount: Object.keys(packages).length
    });
  }, [shows, packages, user?.id]);

  // Compute derived state
  const showsWithPackages = useMemo(() => {
    const showArray = Object.values(shows);
    return showArray.map(show => ({
      show,
      packageTier: packages[show.id]?.tier || 'set-video' as const,
      packagePrice: packages[show.id]?.price
    }));
  }, [shows, packages]);

  const totalCost = useMemo(() => {
    return showsWithPackages.reduce((total, item) => total + (item.packagePrice || 0), 0);
  }, [showsWithPackages]);

  const hasCustomPackage = useMemo(() => {
    return showsWithPackages.some(item => item.packageTier === 'custom');
  }, [showsWithPackages]);

  // Note: With Zustand, no manual state management or event listeners needed!
  // The usePlanStore hooks above automatically re-render when data changes

  const handleRemoveShow = (showId: number) => {
    console.log('🔄 [SIDEBAR] Removing show:', showId);
    PlanStorage.removeShow(showId, user?.id);
    // Event system will handle pricing recalculation
  };

  const handleClearAll = () => {
    console.log('🔄 [SIDEBAR] Clearing all shows');
    PlanStorage.clearShows(user?.id);
    // Event system will handle updates
  };

  // Get package tier styling to match ShowListItem
  const getPackageStyles = (tier: string) => {
    switch (tier) {
      case 'set-core':
        return {
          borderColor: 'border-l-gray-600',
          bgColor: 'bg-gray-50/80',
          cardBg: 'bg-white/70 border-gray-200/60',
          badgeColor: 'bg-gray-600',
          accentColor: 'text-gray-700',
          icon: Smartphone,
          label: 'SET Core'
        };
      case 'set-audio':
        return {
          borderColor: 'border-l-blue-600',
          bgColor: 'bg-blue-50/80',
          cardBg: 'bg-blue-50/60 border-blue-200/60',
          badgeColor: 'bg-blue-600',
          accentColor: 'text-blue-700',
          icon: Speaker,
          label: 'SET Audio'
        };
      case 'set-video':
        return {
          borderColor: 'border-l-purple-600',
          bgColor: 'bg-purple-50/80',
          cardBg: 'bg-gradient-to-br from-purple-50/70 to-blue-50/60 border-purple-200/60',
          badgeColor: 'bg-gradient-to-r from-purple-600 to-blue-600',
          accentColor: 'text-purple-700',
          icon: Video,
          label: 'SET Video'
        };
      case 'custom':
        return {
          borderColor: 'border-l-green-600',
          bgColor: 'bg-green-50/80',
          cardBg: 'bg-gradient-to-br from-green-50/70 to-emerald-50/60 border-green-200/60',
          badgeColor: 'bg-green-600',
          accentColor: 'text-green-700',
          icon: Sliders,
          label: 'SET Custom'
        };
      default:
        return {
          borderColor: 'border-l-gray-500',
          bgColor: 'bg-gray-50/80',
          cardBg: 'bg-white/70 border-gray-200/60',
          badgeColor: 'bg-gray-600',
          accentColor: 'text-gray-700',
          icon: Smartphone,
          label: 'Package'
        };
    }
  };

  const getPackageBadge = (tier: string) => {
    const styles = getPackageStyles(tier);
    const IconComponent = styles.icon;
    
    return (
      <Badge className={`${styles.badgeColor} text-white flex items-center gap-1 text-xs px-2 py-1 shadow-sm`}>
        <IconComponent className="w-2.5 h-2.5" />
        {styles.label}
      </Badge>
    );
  };

  const totalShows = showsWithPackages.length;

  // Mobile: Expandable sticky bar
  const MobileView = () => (
    <div className="lg:hidden">
      {/* Sticky Bar */}
      <div className="fixed top-16 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b shadow-sm">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShoppingCart className="w-5 h-5 text-gray-600" />
              <div>
                <span className="font-medium text-gray-900">
                  {totalShows} show{totalShows !== 1 ? 's' : ''} in plan
                </span>
                {isPricingVisible && (hasCustomPackage || totalCost > 0) && (
                  hasCustomPackage ? (
                    <div className="text-xs font-medium text-gray-600 bg-gray-50 px-2 py-1 rounded-lg border border-gray-200">
                      Contact for Pricing
                    </div>
                  ) : (
                    <div className="text-sm font-bold text-green-700 bg-green-50/80 px-2 py-1 rounded-lg border border-green-200/60">
                      ${totalCost.toLocaleString()} total
                      {isUpdatingPricing && <span className="text-gray-500 ml-1 font-normal">(updating...)</span>}
                    </div>
                  )
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {totalShows > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="flex items-center gap-1"
                >
                  {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  {isExpanded ? 'Hide' : 'Show'}
                </Button>
              )}
              <Link href="/plans">
                <Button size="sm" disabled={totalShows === 0}>
                  Build Plan
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Expandable Content */}
        {isExpanded && totalShows > 0 && (
          <div className="border-t bg-white max-h-80 overflow-y-auto">
            <div className="p-4 space-y-3">
              {showsWithPackages.map((item, index) => {
                const packageStyles = getPackageStyles(item.packageTier);
                return (
                <div key={item.show.id} className={`relative flex items-start gap-3 p-3 rounded-lg ${packageStyles.cardBg} ${packageStyles.borderColor} border-l-4 border-y border-r shadow-sm group`}>
                  {/* Artist Thumbnail - Bigger */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-200 rounded-lg flex items-center justify-center overflow-hidden">
                      {item.show.artist.imageUrl ? (
                        <img 
                          src={item.show.artist.imageUrl} 
                          alt={item.show.artist.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-2xl">🎤</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Content area now takes full width */}
                  <div className="flex-1 min-w-0 flex flex-col gap-0.5 pr-2">
                    {/* Artist Name */}
                    <p className="font-medium text-sm text-gray-900 truncate leading-tight">
                      {item.show.artist.name}
                    </p>
                    
                    {/* Date */}
                    <div className="flex items-center gap-1 text-xs text-gray-600 leading-none">
                      <Calendar className="w-2.5 h-2.5" />
                      {formatDate(item.show.date)}
                    </div>
                    
                    {/* Venue */}
                    <div className="flex items-center gap-1 text-xs text-gray-600 leading-none">
                      <MapPin className="w-2.5 h-2.5 flex-shrink-0" />
                      <span className="truncate">{item.show.venue.name}</span>
                    </div>

                    {/* Capacity */}
                    {item.show.venue.capacity && (
                      <div className="flex items-center gap-1 text-xs text-gray-600 leading-none">
                        <Users className="w-2.5 h-2.5 flex-shrink-0" />
                        <span>{formatCapacity(item.show.venue.capacity)}</span>
                      </div>
                    )}
                    
                    {/* Package Tier and Price Row - now has full width */}
                    <div className="mt-0.5 flex items-center justify-between w-full">
                      {getPackageBadge(item.packageTier)}
                      {isPricingVisible && item.packageTier !== 'custom' && item.packagePrice && (
                        <div className={`text-xs font-bold ${packageStyles.accentColor} bg-white/80 px-2 py-1 rounded-md border border-slate-200/60 shadow-sm`}>
                          ${item.packagePrice.toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Floating trash can overlay */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveShow(item.show.id)}
                    className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity text-red-600 hover:text-red-700 hover:bg-red-50/90 backdrop-blur-sm shadow-sm h-7 w-7 p-0"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                );
              })}
              
              {totalShows > 1 && (
                <div className="pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearAll}
                    className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-600"
                  >
                    Clear All Shows
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Desktop: Right sidebar
  const DesktopView = () => (
    <div className="hidden lg:block">
      {/* Sidebar */}
      <div className={`
        fixed right-0 top-16 bottom-0 w-96 bg-white/80 backdrop-blur-xl shadow-xl transform transition-transform duration-300 z-40 border-l border-white/20
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/20 bg-white/10 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-gray-600" />
              <h2 className="font-semibold text-gray-900">Plan Builder</h2>
            </div>
            <Button variant="ghost" size="sm" onClick={onToggle}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {totalShows === 0 ? (
              <div className="flex-1 flex items-center justify-center p-6">
                <div className="text-center">
                  <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-medium text-gray-900 mb-2">No shows selected</h3>
                  <p className="text-sm text-gray-600">
                    Browse shows and add them to your plan to get started.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Show Count & Total Cost */}
                <div className="p-4 border-b border-slate-200/50 bg-gradient-to-r from-slate-50/90 to-white/90 backdrop-blur-sm shadow-sm">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-800">
                      {totalShows} show{totalShows !== 1 ? 's' : ''} selected
                    </p>
                    {isPricingVisible && (hasCustomPackage || totalCost > 0) && (
                      hasCustomPackage ? (
                        <div className="text-sm font-medium text-gray-600 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
                          Contact for Pricing
                        </div>
                      ) : (
                        <div className="text-lg font-bold text-green-700 bg-green-50/80 px-3 py-1 rounded-lg border border-green-200/60">
                          ${totalCost.toLocaleString()}
                          {isUpdatingPricing && <span className="text-slate-500 ml-1 text-sm font-normal">(updating...)</span>}
                        </div>
                      )
                    )}
                  </div>
                </div>

                {/* Shows List */}
                <div ref={scrollContainerRef} className="flex-1 overflow-y-auto overscroll-contain">
                  <div className="p-4 space-y-3">
                  {showsWithPackages.map((item, index) => {
                    const packageStyles = getPackageStyles(item.packageTier);
                    return (
                    <Card key={item.show.id} className={`group hover:shadow-lg transition-all duration-200 ${packageStyles.cardBg} ${packageStyles.borderColor} border-l-4 border-y border-r shadow-sm relative`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4 w-full">
                          {/* Artist Thumbnail - Bigger */}
                          <div className="flex-shrink-0">
                            <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-200 rounded-lg flex items-center justify-center overflow-hidden">
                              {item.show.artist.imageUrl ? (
                                <img 
                                  src={item.show.artist.imageUrl} 
                                  alt={item.show.artist.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <span className="text-2xl">🎤</span>
                              )}
                            </div>
                          </div>
                          
                          {/* Content area now takes full width */}
                          <div className="flex-1 min-w-0 flex flex-col gap-1 pr-2">
                            {/* Artist Name */}
                            <p className="font-medium text-sm text-gray-900 truncate leading-tight">
                              {item.show.artist.name}
                            </p>
                            
                            {/* Date */}
                            <div className="flex items-center gap-1 text-xs text-gray-600 leading-none">
                              <Calendar className="w-3 h-3" />
                              {formatDate(item.show.date)}
                            </div>
                            
                            {/* Venue */}
                            <div className="flex items-center gap-1 text-xs text-gray-600 leading-none">
                              <MapPin className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{item.show.venue.name}</span>
                            </div>

                            {/* Capacity */}
                            {item.show.venue.capacity && (
                              <div className="flex items-center gap-1 text-xs text-gray-600 leading-none">
                                <Users className="w-3 h-3 flex-shrink-0" />
                                <span>{formatCapacity(item.show.venue.capacity)}</span>
                              </div>
                            )}
                            
                            {/* Package Tier and Price Row - now has full width */}
                            <div className="mt-1 flex items-center justify-between w-full">
                              {getPackageBadge(item.packageTier)}
                              {isPricingVisible && item.packageTier !== 'custom' && item.packagePrice && (
                                <div className={`text-sm font-bold ${packageStyles.accentColor} bg-white/80 px-2 py-1 rounded-md border border-slate-200/60 shadow-sm`}>
                                  ${item.packagePrice.toLocaleString()}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Floating trash can overlay */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveShow(item.show.id)}
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-red-600 hover:text-red-700 hover:bg-red-50/90 backdrop-blur-sm shadow-sm h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </CardContent>
                    </Card>
                    );
                  })}
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="p-4 border-t border-slate-200/50 bg-gradient-to-r from-slate-50/90 to-white/90 backdrop-blur-sm space-y-3 shadow-sm">
                  <Link href="/plans">
                    <Button className="w-full">
                      Build Plan ({totalShows} show{totalShows !== 1 ? 's' : ''})
                    </Button>
                  </Link>
                  
                  {totalShows > 1 && (
                    <Button
                      variant="outline"
                      onClick={handleClearAll}
                      className="w-full text-red-600 border-red-300 bg-white/80 hover:bg-red-50 hover:text-red-700 hover:border-red-400 font-medium transition-all duration-200"
                    >
                      Clear All Shows
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={className}>
      <MobileView />
      <DesktopView />
    </div>
  );
}